import { useController, type Control } from "react-hook-form";

import { useTaskProjectOptions } from "@/features/tasks/hooks/useTaskProjectOptions";
import type { NewTaskInput, NewTaskOutput } from "@/features/tasks/lib/newTaskForm";
import { useT } from "@/shared/i18n/useT";
import { Combobox } from "@/shared/ui/Combobox";
import { Field, describedBy } from "@/shared/ui/Field";

interface NewTaskProjectFieldProps {
  control: Control<NewTaskInput, unknown, NewTaskOutput>;
  error: string | undefined;
  /** Called with the new project, so the form can drop assignees who are not on it. */
  onProjectChange: (projectKey: string | null) => void;
}

/** Where the new task goes: a project the person may add tasks to, or no project (the default). */
export function NewTaskProjectField({ control, error, onProjectChange }: NewTaskProjectFieldProps) {
  const t = useT();
  const projects = useTaskProjectOptions();
  const project = useController({ control, name: "project_key" });

  return (
    <Field id="task-project" label={t("newTask.projectLabel")} error={error}>
      <Combobox
        size="md"
        options={projects.options}
        value={project.field.value}
        onChange={(value) => {
          if (value === project.field.value) return;
          project.field.onChange(value);
          onProjectChange(value);
        }}
        allowClear
        placeholder={t("task.noProject")}
        searchPlaceholder={t("task.projectPicker.search")}
        emptyLabel={t("task.projectPicker.empty")}
        {...describedBy("task-project", error)}
      />
    </Field>
  );
}
